import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import classNames from 'classnames';

import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useMediaQuery } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import CloseIcon from '@material-ui/icons/Close';
import Drawer from '@material-ui/core/Drawer';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import MenuIcon from '@material-ui/icons/Menu';

import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import styles from './styles';
import Header from '../Header/Header';
import SideBar from '../../SideBar/SideBar';
import logo from '../../../images/logo.svg';
import { Routers } from '../../../config/constants';
import { Employees } from '../../../entities';
import { signOut } from '../../../actions/auth/AuthActions';
import { IAppStore } from '../../../types/IAppStore';
import { IAppActions } from '../../../types/IAppActions';
import LogoutConfirmationModal from '../LogoutConfirmationModal/LogoutConfirmationModal';

const useStyles = makeStyles(styles as any);

interface IProps {
  isRegistrationCompleted: boolean;
  pathname: string;
  handleOpenReserveCreateModal?: () => void;
  reservationsIsOpen: boolean;
  signOut: typeof signOut;
}

const DrawerAndHeaderComp: React.FC<IProps> = ({
  isRegistrationCompleted,
  pathname,
  handleOpenReserveCreateModal,
  reservationsIsOpen,
  signOut,
}) => {
  const classes = useStyles();
  const [open, changeVisible] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);

  const { data: currentEmployee } = Employees.useCurrentEmployee();

  const handleLogoutClick = useCallback(() => {
    changeVisible(false);
    setLogoutModalOpen(true);
  }, []);

  const handleLogoutConfirm = useCallback(() => {
    setLogoutModalOpen(false);
    signOut();
  }, [signOut]);

  const handleLogoutCancel = useCallback(() => {
    setLogoutModalOpen(false);
  }, []);

  /** @description Ручка для открытия/закрытия главного меню */
  const handleSwitchDrawerOpen = useCallback(() => {
    changeVisible(!open);
  }, [open]);

  /** @description Ручка для закрытия главного меню */
  const handleSwitchDrawerClose = useCallback(() => {
    changeVisible(false);
  }, []);

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('lg'));

  // Предзагрузка логотипа для быстрого отображения при открытии Drawer
  useEffect(() => {
    // Предзагрузка через link preload
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = logo;
    document.head.appendChild(link);

    // Дополнительная предзагрузка через скрытое изображение
    const img = new Image();
    img.src = logo;

    return () => {
      const existingLink = document.head.querySelector(`link[href="${logo}"]`);
      if (existingLink) {
        document.head.removeChild(existingLink);
      }
    };
  }, [logo]);

  return (
    <Fragment>
      <AppBar
        position="fixed"
        className={classes.appBar}
      >
        <Toolbar className={classes.toolbar}>
          {currentEmployee && (
            <IconButton
              aria-label="Open drawer"
              onClick={handleSwitchDrawerOpen}
              className={classes.menuButton}
            >
              {
                open
                  ? <CloseIcon style={{ fontSize: 32, fill: '#000' }} />
                  : <MenuIcon style={{ fontSize: 32, fill: '#000' }} />
              }
            </IconButton>
          )}

          <Header isRegistrationCompleted={isRegistrationCompleted} />
        </Toolbar>
      </AppBar>
      <Drawer
        variant={matches ? 'permanent' : 'temporary'}
        className={classNames({ [classes.drawerOpen]: matches })}
        classes={{ paper: classes.drawerOpen }}
        open={open}
        onClose={handleSwitchDrawerClose}
      >
        {reservationsIsOpen && <div className={classes.isDisabled} />}
        <div className={classNames(classes.toolbar, classes.logoBlock)}>
          <NavLink to={Routers.home}>
            <img
              className={classes.logo}
              src={logo}
              alt="logo"
              loading="eager"
              decoding="sync"
            />
          </NavLink>
        </div>

        <SideBar
          isRegistrationCompleted={isRegistrationCompleted}
          pathname={pathname}
          handleSwitchDrawerClose={handleSwitchDrawerClose}
          handleOpenReserveCreateModal={handleOpenReserveCreateModal}
          onLogout={handleLogoutClick}
        />
      </Drawer>
      <LogoutConfirmationModal
        isOpen={logoutModalOpen}
        handleClose={handleLogoutCancel}
        handleConfirm={handleLogoutConfirm}
      />
    </Fragment>
  );
};

const mapStateToProps = (state: IAppStore) => ({
  user: state.auth.user,
});

const mapDispatchToProps = (dispatch: Dispatch<IAppActions>) => bindActionCreators({
  signOut,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(DrawerAndHeaderComp);
